import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Header } from '../components/ui/header';
import { Footer } from '../components/ui/footer';
import { UniversityCard } from '../components/ui/university-card';
import { ListingFilters } from '../components/filters/ListingFilters';
import { API_BASE } from '../config/api';

export const Universities = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [universities, setUniversities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Unified filter state - Initialize from URL params
  const [filters, setFilters] = useState({
    searchQuery: searchParams.get('search') || '',
    selectedCountry: searchParams.get('country') || '',
    selectedState: searchParams.get('state') || '',
    selectedInstitutionType: searchParams.get('type') || '',
    selectedLevel: searchParams.get('level') || '',
    selectedField: searchParams.get('field') || '',
    selectedSubjects: searchParams.get('subjects') ? searchParams.get('subjects').split(',') : []
  });
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [mobileDisplayCount, setMobileDisplayCount] = useState(8);
  const [isMobile, setIsMobile] = useState(false);
  
  const ITEMS_PER_PAGE = 16; // 4 columns x 4 rows
  
  // Fetch universities from API
  useEffect(() => {
    const fetchUniversities = async () => {
      try {
        setLoading(true);
        const backendUrl = API_BASE;
        const response = await fetch(`${backendUrl}/api/public/universities`);
        
        if (!response.ok) {
          throw new Error('Failed to fetch universities');
        }
        
        const data = await response.json();
        setUniversities(data.universities || []);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    
    fetchUniversities();
  }, []);
  
  // Check if mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  // Get states for Malaysia
  const malaysianStates = ['Kuala Lumpur', 'Selangor', 'Penang', 'Johor', 'Perak', 'Kedah', 'Kelantan', 'Terengganu', 'Pahang', 'Negeri Sembilan', 'Melaka', 'Sabah', 'Sarawak', 'Perlis', 'Labuan', 'Putrajaya'];
  
  // Filter universities (memoized to avoid recalculation on every render)
  const filteredUniversities = useMemo(() => {
    return universities.filter(uni => {
      // Search filter
      const searchLower = filters.searchQuery.toLowerCase();
      const matchesSearch = !filters.searchQuery || 
        uni.name.toLowerCase().includes(searchLower) ||
        uni.display_name?.toLowerCase().includes(searchLower) ||
        uni.country.toLowerCase().includes(searchLower) ||
        (uni.fields_of_study || []).some(field => field.toLowerCase().includes(searchLower));
      
      const matchesCountry = !filters.selectedCountry || uni.country === filters.selectedCountry;
      const matchesState = !filters.selectedState || uni.city === filters.selectedState;
      const matchesType = !filters.selectedInstitutionType || uni.institution_type === filters.selectedInstitutionType;
      const matchesLevel = !filters.selectedLevel || (uni.levels_offered || []).includes(filters.selectedLevel);
      
      // Field of Study filter
      const matchesField = !filters.selectedField || (uni.fields_of_study || []).includes(filters.selectedField);
      
      // Subject filter (if subjects are present in data)
      const matchesSubject = filters.selectedSubjects.length === 0 || 
        filters.selectedSubjects.some(subject => (uni.fields_of_study || []).includes(subject));
      
      return matchesSearch && matchesCountry && matchesState && matchesType && matchesLevel && matchesField && matchesSubject;
    });
  }, [universities, filters]);
  
  // Calculate pagination
  const totalPages = Math.ceil(filteredUniversities.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  
  // Apply pagination
  const displayedUniversities = isMobile 
    ? filteredUniversities.slice(0, mobileDisplayCount)
    : filteredUniversities.slice(startIndex, endIndex);
  
  const hasMoreToShow = isMobile && mobileDisplayCount < filteredUniversities.length;
  
  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters]);
  
  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    // Reset state filter if country changes from Malaysia
    if (newFilters.selectedCountry !== 'Malaysia') {
      setFilters(prev => ({ ...prev, selectedState: '' }));
    }
    
    // Update URL params
    const params = {};
    if (newFilters.searchQuery) params.search = newFilters.searchQuery;
    if (newFilters.selectedCountry && newFilters.selectedCountry !== 'Malaysia') params.country = newFilters.selectedCountry;
    if (newFilters.selectedState) params.state = newFilters.selectedState;
    if (newFilters.selectedInstitutionType) params.type = newFilters.selectedInstitutionType;
    if (newFilters.selectedLevel) params.level = newFilters.selectedLevel;
    if (newFilters.selectedField) params.field = newFilters.selectedField;
    if (newFilters.selectedSubjects && newFilters.selectedSubjects.length > 0) {
      params.subjects = newFilters.selectedSubjects.join(',');
    }
    setSearchParams(params);
  };
  
  const loadMore = () => {
    setMobileDisplayCount(prev => prev + 8);
  };
  
  // Pagination component
  const Pagination = ({ position = 'bottom' }) => {
    if (totalPages <= 1) return null;
    return (
      <div className={`hidden md:flex items-center gap-3 ${position === 'top' ? 'justify-end mb-6' : 'justify-center mt-8'}`}>
        <button
          onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
          disabled={currentPage === 1}
          className="w-10 h-10 flex items-center justify-center bg-white border border-[#D1D5DB] rounded-lg text-sm font-medium text-[#6B7280] hover:bg-[#F9FAFB] hover:border-[#9CA3AF] disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-sm"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        
        <div className="flex items-center gap-1">
          {[...Array(Math.min(totalPages, 5))].map((_, idx) => {
            let pageNum;
            if (totalPages <= 5) {
              pageNum = idx + 1;
            } else if (currentPage <= 3) {
              pageNum = idx + 1;
            } else if (currentPage >= totalPages - 2) {
              pageNum = totalPages - 4 + idx;
            } else {
              pageNum = currentPage - 2 + idx;
            }
            return (
              <button
                key={pageNum}
                onClick={() => setCurrentPage(pageNum)}
                className={`min-w-[36px] h-9 px-3 rounded-lg text-sm font-medium transition-all ${
                  currentPage === pageNum
                    ? 'bg-[#111827] text-white shadow-md'
                    : 'bg-white border border-[#E5E7EB] text-[#6B7280] hover:bg-[#F9FAFB] hover:border-[#9CA3AF]'
                }`}
              >
                {pageNum}
              </button>
            );
          })}
        </div>
        
        <button
          onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
          disabled={currentPage === totalPages}
          className="w-10 h-10 flex items-center justify-center bg-white border border-[#D1D5DB] rounded-lg text-sm font-medium text-[#6B7280] hover:bg-[#F9FAFB] hover:border-[#9CA3AF] disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-sm"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>
    );
  };
  
  const handleExploreClick = (university) => {
    // Navigate to university detail page using slug if available, fallback to ID
    if (university?.slug) {
      window.location.href = `/universities/${university.slug}`;
    } else if (university?.id) {
      // Fallback to ID if slug not available (backward compatibility)
      window.location.href = `/universities/${university.id}`;
    }
  };
  
  return (
    <div className="min-h-screen bg-[#FFFDFB] flex flex-col">
      <Header />
      
      <main className="flex-1">
        {/* Page Header Section */}
        <section className="bg-gradient-to-b from-[#FBF9F7] to-[#FFFDFB] py-12 md:py-16">
          <div className="container mx-auto px-6 lg:px-10 max-w-[1280px]">
            <h1 
              className="text-[#111827] mb-4 text-center" 
              style={{ fontSize: '2.5rem', fontWeight: 750, lineHeight: '1.2' }}
            >
              Explore Universities & Institutions
            </h1>
            <p className="text-[#5A6978] text-center max-w-2xl mx-auto" style={{ fontSize: '1.125rem', lineHeight: '1.6' }}>
              Discover world-class universities and institutions that align with your academic goals and aspirations.
            </p>
          </div>
        </section>
        
        {/* Filter Bar & Results Section */}
        <section className="py-12">
          <div className="container mx-auto px-6 lg:px-10 max-w-[1280px]">
            {/* Unified Filter Component */}
            <ListingFilters 
              context="universities"
              filters={filters}
              onFilterChange={handleFilterChange}
              resultCount={filteredUniversities.length}
            />
            
            {/* State Filter (Malaysia-specific, shown below main filters) */}
            {filters.selectedCountry === 'Malaysia' && (
              <div className="bg-white border border-[#E5E7EB] rounded-xl p-4 mb-8 shadow-sm">
                <label className="block text-sm font-semibold text-[#111827] mb-2">State/City (Malaysia)</label>
                <select
                  value={filters.selectedState}
                  onChange={(e) => handleFilterChange({ ...filters, selectedState: e.target.value })}
                  className="w-full md:w-64 px-4 py-2.5 border border-[#E5E7EB] rounded-lg bg-white text-[#2C3E50] focus:outline-none focus:border-[#1E40AF] focus:ring-1 focus:ring-[#1E40AF] transition-colors"
                >
                  <option value="">All States</option>
                  {malaysianStates.map(state => (
                    <option key={state} value={state}>{state}</option>
                  ))}
                </select>
              </div>
            )}
            
            {/* Loading State */}
            {loading && (
              <div className="text-center py-16">
                <div className="inline-block w-8 h-8 border-4 border-[#F97316] border-t-transparent rounded-full animate-spin"></div>
                <p className="mt-4 text-[#5A6978]">Loading universities...</p>
              </div>
            )}
            
            {/* Error State */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
                <p className="text-red-600 font-medium">Failed to load universities</p>
                <p className="text-sm text-red-500 mt-2">{error}</p>
              </div>
            )}
            
            {/* Empty State */}
            {!loading && !error && filteredUniversities.length === 0 && (
              <div className="bg-[#FBF9F7] border border-[#E5E7EB] rounded-xl p-12 text-center">
                <svg className="w-16 h-16 mx-auto mb-4 text-[#8B9AAB]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
                <h3 className="text-xl font-semibold text-[#111827] mb-2">No universities found</h3>
                <p className="text-[#5A6978] mb-6">
                  We couldn't find any universities matching your criteria. Try adjusting your filters or search query.
                </p>
                <button
                  onClick={() => handleFilterChange({
                    searchQuery: '',
                    selectedCountry: 'Malaysia',
                    selectedState: '',
                    selectedInstitutionType: '',
                    selectedLevel: '',
                    selectedField: '',
                    selectedSubjects: []
                  })}
                  className="px-6 py-3 bg-[#D4A853] hover:bg-[#B8923F] text-white font-semibold rounded-lg transition-colors"
                >
                  Clear Filters
                </button>
              </div>
            )}
            
            {/* University Cards Grid */}
            {!loading && !error && displayedUniversities.length > 0 && (
              <>
                {/* Top Pagination */}
                <div className="flex items-center justify-between mb-4">
                  <p className="text-sm text-[#6B7280]">
                    Showing {startIndex + 1}-{Math.min(endIndex, filteredUniversities.length)} of {filteredUniversities.length} universities
                  </p>
                  <Pagination position="top" />
                </div>
                
                {/* 4 Column Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {displayedUniversities.map(uni => (
                    <UniversityCard
                      key={uni.id}
                      university={uni}
                      ctaText="Explore More"
                      onConsultClick={() => handleExploreClick(uni)}
                    />
                  ))}
                </div>
                
                {/* Bottom Pagination */}
                <Pagination position="bottom" />
                
                {/* View More Button (Mobile) */}
                {hasMoreToShow && (
                  <div className="mt-8 text-center md:hidden">
                    <button
                      onClick={loadMore}
                      className="px-8 py-3 bg-white border-2 border-[#F97316] text-[#F97316] hover:bg-[#F97316] hover:text-white font-semibold rounded-lg transition-colors"
                    >
                      View More Universities
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
};

export default Universities;
